// Board issue drawer — fixed right panel, write-through to Jira (Screen 3).
import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../../design/brainTheme";
import { Icon, MonoLabel } from "../../../mui";
import {
  getJiraIssueDetail,
  getProjectJiraTransitions,
  projectJiraAction,
  listJiraAssignableUsers,
  type JiraIssueDetail,
  type JiraTransitionOption,
  type JiraAssignableUser,
} from "../../../lib/brain-api";

const c = brainTokens.color;
const MONO = brainTokens.font.mono;

const selectSx = {
  fontSize: 12.5,
  fontFamily: brainTokens.font.sans,
  color: c.ink2,
  background: c.pageBg,
  border: `1px solid ${c.border}`,
  borderRadius: `${brainTokens.radius.chip}px`,
  padding: "7px 9px",
  outline: "none",
  width: "100%",
  cursor: "pointer",
};

export function IssueDrawer({
  projectId,
  issueKey,
  boardName,
  onClose,
  onChanged,
}: {
  projectId: string;
  issueKey: string | null;
  boardName?: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [detail, setDetail] = useState<JiraIssueDetail | null>(null);
  const [transitions, setTransitions] = useState<JiraTransitionOption[] | null>(null);
  const [users, setUsers] = useState<JiraAssignableUser[]>([]);
  const [comment, setComment] = useState("");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const issueKeyRef = useRef(issueKey);
  issueKeyRef.current = issueKey;

  useEffect(() => {
    setDetail(null);
    setTransitions(null);
    setComment("");
    setSummary("");
    setBusy(false);
    if (!issueKey) return;
    const key = issueKey;
    void getJiraIssueDetail(key)
      .then((d) => {
        if (issueKeyRef.current !== key) return;
        setDetail(d);
        setSummary(d.summary);
      })
      .catch(() => {
        if (issueKeyRef.current === key) setDetail(null);
      });
    void getProjectJiraTransitions(projectId, key)
      .then((t) => {
        if (issueKeyRef.current === key) setTransitions(t);
      })
      .catch(() => {
        if (issueKeyRef.current === key) setTransitions([]);
      });
  }, [issueKey, projectId]);

  if (!issueKey) return null;

  const act = async (body: Parameters<typeof projectJiraAction>[2], okMsg: string) => {
    const key = issueKey;
    setBusy(true);
    try {
      await projectJiraAction(projectId, key, body);
      // Board must refresh even if the drawer moved on — write already landed.
      onChanged();
      if (issueKeyRef.current !== key) return;
      enqueueSnackbar(okMsg, { variant: "success" });
      const d = await getJiraIssueDetail(key).catch(() => null);
      if (issueKeyRef.current !== key) return;
      if (d) {
        setDetail(d);
        setSummary(d.summary);
      }
      if (body.action === "transition") {
        void getProjectJiraTransitions(projectId, key)
          .then((t) => {
            if (issueKeyRef.current === key) setTransitions(t);
          })
          .catch(() => {
            if (issueKeyRef.current === key) setTransitions([]);
          });
      }
    } catch (e) {
      if (issueKeyRef.current === key) {
        enqueueSnackbar(e instanceof Error ? e.message : "Jira write failed", { variant: "error" });
      }
    } finally {
      setBusy(false);
    }
  };

  const commitSummary = () => {
    if (!detail || busy) return;
    const next = summary.trim();
    if (!next || next === detail.summary) {
      setSummary(detail.summary);
      return;
    }
    void act({ action: "edit", summary: next }, "Summary updated");
  };

  const postComment = () => {
    const body = comment.trim();
    if (!body || busy) return;
    void act({ action: "comment", body }, "Comment posted").then(() => setComment(""));
  };

  const top = brainTokens.layout.topBarHeight;

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label={detail ? `Issue ${detail.key}` : `Issue ${issueKey}`}
      sx={{
        position: "fixed",
        top,
        right: 30,
        bottom: 24,
        width: 424,
        maxWidth: "calc(100vw - 48px)",
        background: c.pageBg,
        borderLeft: `1px solid ${c.border}`,
        borderTop: `1px solid ${c.border}`,
        boxShadow: brainTokens.shadow.drawer,
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
        borderRadius: `0 0 ${brainTokens.radius.card}px 0`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          px: "16px",
          py: "14px",
          borderBottom: `1px solid ${c.borderLight}`,
          flex: "none",
        }}
      >
        <Box
          component="a"
          href={detail?.url ?? "#"}
          target="_blank"
          rel="noreferrer"
          sx={{
            fontFamily: MONO,
            fontSize: 13,
            color: c.tealInk,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {detail?.key ?? issueKey}
        </Box>
        {boardName && (
          <Box sx={{ fontSize: 11.5, color: c.muted2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {boardName}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Box
          component="a"
          href={detail?.url ?? "#"}
          target="_blank"
          rel="noreferrer"
          sx={{
            fontSize: 12.5,
            fontWeight: 500,
            color: c.tealInk,
            textDecoration: "none",
            whiteSpace: "nowrap",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Open in Jira ↗
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            border: "none",
            background: "transparent",
            color: c.muted,
            cursor: "pointer",
            borderRadius: `${brainTokens.radius.chip}px`,
            p: 0,
            "&:hover": { color: c.ink2, background: c.panelBg2 },
          }}
        >
          <Icon name="close" size={16} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", px: "16px", py: "16px", display: "flex", flexDirection: "column", gap: "16px" }}>
        {!detail ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={22} sx={{ color: c.teal }} />
          </Box>
        ) : (
          <>
            <Box
              component="input"
              value={summary}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSummary(e.target.value)}
              onBlur={commitSummary}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                }
              }}
              disabled={busy}
              sx={{
                fontSize: 15,
                fontWeight: 500,
                lineHeight: 1.4,
                color: c.ink,
                fontFamily: brainTokens.font.sans,
                border: "1px solid transparent",
                borderRadius: `${brainTokens.radius.chip}px`,
                background: "transparent",
                padding: "6px 8px",
                margin: "-6px -8px",
                outline: "none",
                width: "calc(100% + 16px)",
                "&:hover": { borderColor: c.border },
                "&:focus": { borderColor: c.teal },
              }}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <Box
                component="select"
                disabled={busy || transitions === null}
                value=""
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const id = e.target.value;
                  const t = (transitions ?? []).find((x) => x.id === id);
                  if (t) void act({ action: "transition", transitionId: t.id, toStatus: t.toStatus }, `Moved to ${t.toStatus}`);
                  e.target.value = "";
                }}
                sx={selectSx}
                aria-label="Status"
              >
                <option value="" disabled>
                  {detail.status}
                </option>
                {(transitions ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.toStatus}
                  </option>
                ))}
                {transitions === null && (
                  <option value="" disabled>
                    Loading…
                  </option>
                )}
              </Box>

              <Autocomplete
                size="small"
                options={users}
                getOptionLabel={(u) => u.displayName}
                onOpen={() => {
                  if (!users.length) void listJiraAssignableUsers().then(setUsers).catch(() => setUsers([]));
                }}
                onChange={(_, u) =>
                  void act(
                    { action: "assign", accountId: u ? u.accountId : null, assigneeName: u?.displayName },
                    u ? `Assigned to ${u.displayName}` : "Unassigned"
                  )
                }
                disabled={busy}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={detail.assignee ?? "Assignee"}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: 12.5,
                        padding: "0 !important",
                        background: c.pageBg,
                        "& fieldset": { borderColor: c.border, borderRadius: `${brainTokens.radius.chip}px` },
                        "&:hover fieldset": { borderColor: c.border },
                        "&.Mui-focused fieldset": { borderColor: c.teal, borderWidth: 1 },
                        "& .MuiAutocomplete-input": { padding: "7px 9px !important" },
                      },
                    }}
                  />
                )}
              />
            </Box>

            {detail.description && (
              <Box>
                <MonoLabel size={10} color={c.faint} sx={{ display: "block", mb: "8px" }}>
                  Description
                </MonoLabel>
                <Box sx={{ fontSize: 13, color: c.ink3, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{detail.description}</Box>
              </Box>
            )}

            {detail.checklist.length > 0 && (
              <Box>
                <MonoLabel size={10} color={c.faint} sx={{ display: "block", mb: "8px" }}>
                  Checklist
                </MonoLabel>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {detail.checklist.map((it, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                        fontSize: 13,
                        color: it.checked ? c.muted2 : c.ink3,
                        textDecoration: it.checked ? "line-through" : "none",
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          color: it.checked ? c.teal : c.placeholder,
                          flex: "none",
                          mt: "1px",
                        }}
                      >
                        <Icon name={it.checked ? "square-check" : "square"} size={15} />
                      </Box>
                      <Box component="span">{it.text}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Box>
              <MonoLabel size={10} color={c.faint} sx={{ display: "block", mb: "8px" }}>
                Comments {detail.commentCount}
              </MonoLabel>
              <Box
                component="textarea"
                value={comment}
                disabled={busy}
                placeholder="Add a comment…"
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    postComment();
                  }
                }}
                sx={{
                  width: "100%",
                  minHeight: 64,
                  fontSize: 13,
                  fontFamily: brainTokens.font.sans,
                  color: c.ink2,
                  lineHeight: 1.5,
                  border: `1px solid ${c.border}`,
                  borderRadius: `${brainTokens.radius.card}px`,
                  padding: "10px 12px",
                  resize: "vertical",
                  outline: "none",
                  background: c.pageBg,
                  boxSizing: "border-box",
                  "&:focus": { borderColor: c.teal },
                  "&::placeholder": { color: c.placeholder },
                }}
              />
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          px: "16px",
          py: "12px",
          borderTop: `1px solid ${c.borderLight}`,
          flex: "none",
        }}
      >
        <Box sx={{ fontSize: 11.5, color: c.faint, flex: 1, lineHeight: 1.4 }}>
          Every change writes through to Jira immediately.
        </Box>
        <Box
          component="button"
          type="button"
          onClick={onClose}
          sx={{
            fontSize: 12.5,
            fontFamily: brainTokens.font.sans,
            color: c.muted,
            background: "transparent",
            border: `1px solid ${c.border}`,
            borderRadius: `${brainTokens.radius.chip}px`,
            px: "12px",
            py: "5px",
            cursor: "pointer",
            "&:hover": { color: c.ink2, borderColor: c.hoverBorder },
          }}
        >
          Close
        </Box>
      </Box>
    </Box>
  );
}
